import { Button } from "@/shared/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";
import {
  DELIVERABLES, DELIVERABLE_CONFIG, STATUS_FILTERS, getStatusLabel,
  type DeliverableKey, type StatusFilter,
} from "@/features/properties/services/deliveryStatusConfig";
import {
  EMPTY_FILTERS, hasActiveFilters,
  type DeliverableFilterState, type PropertyFilters,
} from "@/features/properties/services/propertyFilters";
import type { PropertyTeam } from "@/features/properties/services/clientPropertiesQueries";
import { LivePropertySearch } from "@/features/properties/components/LivePropertySearch";

interface Props {
  clientId: string;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onPropertySelect: (id: string) => void;
  filters: PropertyFilters;
  stageCounts: Record<StatusFilter, number>;
  teams: PropertyTeam[];
  onFiltersChange: (filters: PropertyFilters) => void;
}

const NEXT_STATE: Record<DeliverableFilterState, DeliverableFilterState> = {
  any: "has", has: "missing", missing: "any",
};

export function PropertiesFilterBar({
  clientId, searchTerm, onSearchTermChange, onPropertySelect,
  filters, stageCounts, teams, onFiltersChange,
}: Props) {
  const cycleStage = (stage: StatusFilter) => {
    const current = filters.stages[stage] ?? "any";
    onFiltersChange({ ...filters, stages: { ...filters.stages, [stage]: NEXT_STATE[current] } });
  };

  const cycleDeliverable = (key: DeliverableKey) => {
    const current = filters.deliverables[key] ?? "any";
    onFiltersChange({ ...filters, deliverables: { ...filters.deliverables, [key]: NEXT_STATE[current] } });
  };

  const toggleTeam = (id: string) => {
    const teamIds = filters.teamIds.includes(id)
      ? filters.teamIds.filter((t) => t !== id)
      : [...filters.teamIds, id];
    onFiltersChange({ ...filters, teamIds });
  };

  return (
    <div className="mb-3 flex-shrink-0">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border/50 bg-card/60 px-3 py-2.5">
        <div className="w-52">
          <LivePropertySearch
            clientId={clientId}
            value={searchTerm}
            onPropertySelect={onPropertySelect}
            onSearchTermChange={onSearchTermChange}
            showDropdown={false}
            placeholder="Search…"
            compact
          />
        </div>
        <span className="ml-4 text-[11px] uppercase tracking-wide text-muted-foreground">Status</span>
        {STATUS_FILTERS.map((stage) => {
          const state = filters.stages[stage] ?? "any";
          return (
            <button
              key={stage}
              type="button"
              onClick={() => cycleStage(stage)}
              title={state === "any" ? "Click to include" : state === "has" ? "Including — click to exclude" : "Excluding — click to clear"}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-semibold",
                state === "has" && "border-transparent bg-primary/20 text-primary",
                state === "missing" && "border-transparent bg-red-500/20 text-red-400 line-through",
                state === "any" && "border-border bg-muted text-muted-foreground"
              )}
            >
              {getStatusLabel(stage)} {stageCounts[stage] ?? 0}
            </button>
          );
        })}

        <span className="ml-4 text-[11px] uppercase tracking-wide text-muted-foreground">Deliverables</span>
        {DELIVERABLES.map((key) => {
          const state = filters.deliverables[key] ?? "any";
          return (
            <button
              key={key}
              type="button"
              onClick={() => cycleDeliverable(key)}
              title={state === "any" ? "Click to require present" : state === "has" ? "Requiring present — click to require missing" : "Requiring missing — click to clear"}
              className={cn(
                "rounded-full border px-3 py-1 text-xs font-semibold",
                state === "has" && "border-transparent bg-primary/20 text-primary",
                state === "missing" && "border-transparent bg-red-500/20 text-red-400 line-through",
                state === "any" && "border-border bg-muted text-muted-foreground"
              )}
            >
              {DELIVERABLE_CONFIG[key].label}
            </button>
          );
        })}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="ml-4">
              Initiatives{filters.teamIds.length > 0 ? ` (${filters.teamIds.length})` : ""}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-80 p-2">
            {teams.length === 0 ? (
              <p className="px-2 py-3 text-sm text-muted-foreground">No initiatives yet</p>
            ) : (
              teams.map((team) => (
                <button
                  key={team.id}
                  type="button"
                  role="checkbox"
                  aria-checked={filters.teamIds.includes(team.id)}
                  onClick={() => toggleTeam(team.id)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-muted"
                >
                  <span className={cn("h-3.5 w-3.5 shrink-0 rounded border", filters.teamIds.includes(team.id) && "bg-primary")} />
                  <span className="min-w-0 truncate text-sm" title={team.name}>{team.name}</span>
                </button>
              ))
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {hasActiveFilters(filters) && (
          <button type="button" className="ml-auto text-xs text-muted-foreground underline" onClick={() => onFiltersChange(EMPTY_FILTERS)}>
            Clear all
          </button>
        )}
      </div>

      <FilterTokens filters={filters} teams={teams} onFiltersChange={onFiltersChange} />
    </div>
  );
}

function FilterTokens({
  filters, teams, onFiltersChange,
}: Pick<Props, "filters" | "teams" | "onFiltersChange">) {
  const stageTokens = (Object.entries(filters.stages) as [StatusFilter, DeliverableFilterState][])
    .filter(([, s]) => s && s !== "any");
  const deliverableTokens = (Object.entries(filters.deliverables) as [DeliverableKey, DeliverableFilterState][])
    .filter(([, s]) => s && s !== "any");
  const teamTokens = teams.filter((t) => filters.teamIds.includes(t.id));
  if (stageTokens.length === 0 && deliverableTokens.length === 0 && teamTokens.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {stageTokens.map(([stage, state]) => (
        <span key={stage} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-xs">
          {getStatusLabel(stage)}: <span className="capitalize">{state === "missing" ? "excluded" : "included"}</span>
          <button type="button" aria-label={`Clear ${stage}`} onClick={() => onFiltersChange({ ...filters, stages: { ...filters.stages, [stage]: "any" } })}>✕</button>
        </span>
      ))}
      {teamTokens.map((t) => (
        <span key={t.id} className="inline-flex max-w-[260px] items-center gap-1.5 rounded-full border border-primary/30 bg-primary/15 px-2 py-0.5 text-xs text-primary">
          <span className="truncate" title={t.name}>{t.name}</span>
          <button type="button" aria-label={`Remove ${t.name}`} onClick={() => onFiltersChange({ ...filters, teamIds: filters.teamIds.filter((id) => id !== t.id) })}>✕</button>
        </span>
      ))}
      {deliverableTokens.map(([key, state]) => (
        <span key={key} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2 py-0.5 text-xs">
          {DELIVERABLE_CONFIG[key].shortLabel}: <span className="capitalize">{state}</span>
          <button type="button" aria-label={`Clear ${key}`} onClick={() => onFiltersChange({ ...filters, deliverables: { ...filters.deliverables, [key]: "any" } })}>✕</button>
        </span>
      ))}
    </div>
  );
}
